import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type {
  ResponseDto,
  OrganizationTaxRateDto,
  UpdateOrganizationTaxRateDto,
} from "@mimber/schema"
import { taxRatesKeys } from "../tax-rates.utils"
import { taxRateAssignmentsKeys } from "../tax-rates.utils"

type UpdateTaxRateParams = {
  uuid: string
  data: UpdateOrganizationTaxRateDto
}

/**
 * Organization-specific hook for updating a tax rate.
 * Can only be used in mimber-organization app.
 */
export const useUpdateTaxRate = (
  options?: UseMutationOptions<
    ResponseDto<OrganizationTaxRateDto>,
    Error,
    UpdateTaxRateParams
  >
): UseMutationResult<
  ResponseDto<OrganizationTaxRateDto>,
  Error,
  UpdateTaxRateParams
> => {
  guardAppUsage("mimber-organization", "useUpdateTaxRate")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({
      uuid,
      data,
    }: UpdateTaxRateParams): Promise<ResponseDto<OrganizationTaxRateDto>> => {
      const response = await server.patch<ResponseDto<OrganizationTaxRateDto>>(
        `tax-rates/${uuid}`,
        data
      )
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: taxRatesKeys.findAll() })
      // Assignments embed the tax rate object, so refresh those too
      queryClient.invalidateQueries({
        queryKey: taxRateAssignmentsKeys.getAssignments(),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
